import React, { useState, useEffect } from 'react';
import { blogAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const AddEditBlog = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        media_type: 'text',
        media: []
    });
    const [existingMediaUrls, setExistingMediaUrls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);

    useEffect(() => {
        if (isEditMode) {
            fetchBlog();
        }
    }, [id]);

    const fetchBlog = async () => {
        try {
            const response = await blogAPI.getById(id);
            const blog = response.data;
            setFormData({
                title: blog.title,
                content: blog.content,
                media_type: blog.media_type,
                media: []
            });
            // Handle legacy single url or new array
            let urls = [];
            if (blog.media_urls && Array.isArray(blog.media_urls)) {
                urls = blog.media_urls;
            } else if (blog.media_url) {
                urls = [blog.media_url];
            }
            setExistingMediaUrls(urls);
        } catch (error) {
            console.error("Error fetching blog:", error);
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        if (e.target.files) {
            setFormData(prev => ({ ...prev, media: Array.from(e.target.files) }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('content', formData.content);
            data.append('media_type', formData.media_type);
            if (formData.media && formData.media.length > 0) {
                formData.media.forEach(file => {
                    data.append('media', file);
                });
            }

            if (isEditMode) {
                await blogAPI.update(id, data);
            } else {
                await blogAPI.create(data);
            }
            navigate('/admin/blogs');
        } catch (error) {
            console.error("Error saving blog:", error);
            alert("Failed to save blog");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="text-center py-20">Loading...</div>;

    return (
        <div className="pt-24 min-h-screen bg-gray-100 px-6 pb-12">
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md p-8">
                <div className="flex items-center mb-6">
                    <Link to="/admin/blogs" className="text-gray-500 hover:text-gray-700 mr-4">
                        <ArrowLeftIcon className="h-6 w-6" />
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-800">{isEditMode ? 'Edit Blog' : 'Create New Blog'}</h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full border-gray-300 rounded-md shadow-sm p-3 border focus:ring-green-500 focus:border-green-500"
                            placeholder="Enter blog title"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                        <textarea
                            name="content"
                            value={formData.content}
                            onChange={handleChange}
                            required
                            rows="10"
                            className="w-full border-gray-300 rounded-md shadow-sm p-3 border focus:ring-green-500 focus:border-green-500"
                            placeholder="Write your blog content here..."
                        ></textarea>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Media Type</label>
                            <select
                                name="media_type"
                                value={formData.media_type}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-md shadow-sm p-3 border focus:ring-green-500 focus:border-green-500"
                            >
                                <option value="text">Text Only</option>
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                            </select>
                        </div>

                        {formData.media_type !== 'text' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Upload {formData.media_type === 'image' ? 'Image' : 'Video'}
                                </label>
                                <input
                                    type="file"
                                    accept={formData.media_type === 'image' ? 'image/*' : 'video/*'}
                                    multiple
                                    onChange={handleFileChange}
                                    className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-green-500 focus:border-green-500"
                                />
                                {existingMediaUrls.length > 0 && formData.media.length === 0 && (
                                    <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-2">Current Media ({existingMediaUrls.length}):</p>
                                        <div className="flex flex-wrap gap-2">
                                            {existingMediaUrls.map((url, index) => (
                                                <div key={index} className="w-16 h-16 border rounded overflow-hidden">
                                                    {/* Simple preview based on type */}
                                                    {formData.media_type === 'video' ? (
                                                        <video src={getImgUrl(url)} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <img src={getImgUrl(url)} alt="preview" className="w-full h-full object-cover" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {formData.media.length > 0 && (
                                    <p className="text-xs text-green-600 mt-1">{formData.media.length} new file(s) selected (will replace existing)</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full bg-green-600 text-white py-3 rounded-lg font-bold shadow-md hover:bg-green-700 transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? 'Saving...' : (isEditMode ? 'Update Blog' : 'Publish Blog')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEditBlog;
